import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Repeat, RotateCcw } from "lucide-react";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });

const preview = (t) =>
  (t || "")
    .replace(/\{first_name\}/g, "Sarah")
    .replace(/\{tech_credit\}/g, "Screen Tech (Brett)")
    .replace(/\{review_url\}/g, "https://jobs.screenzz.com/r/abc123");

const fmtTime = (hhmm) => {
  const [h, m] = (hhmm || "18:00").split(":").map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export const ReviewFollowupSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/review-followup`, auth()).then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load follow-up settings"));
  }, []);

  if (!cfg) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  const set = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/review-followup`, cfg, auth());
      toast.success("Follow-up settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border p-5 mt-8 space-y-4" data-testid="review-followup-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2"><Repeat className="w-4 h-4 text-[#207024]" /> Automatic follow-up text</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Sends the same tracked link once more to customers who haven't clicked it, aren't marked Reviewed, and haven't texted you back.
          </p>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="w-5 h-5 accent-[#207024]" data-testid="followup-enabled" />
          <span className="text-sm font-medium">{cfg.enabled ? "On" : "Off"}</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-zinc-500">
          Days after first text
          <input type="number" min="1" max="60" value={cfg.days_after}
            onChange={(e) => set({ days_after: e.target.value === "" ? "" : Number(e.target.value) })}
            className="mt-1 w-full h-10 border border-zinc-200 rounded-lg px-3 text-sm text-zinc-900" data-testid="followup-days" />
        </label>
        <label className="text-xs text-zinc-500">
          Send at (Pacific)
          <input type="time" value={cfg.send_time} onChange={(e) => set({ send_time: e.target.value })}
            className="mt-1 w-full h-10 border border-zinc-200 rounded-lg px-3 text-sm text-zinc-900" data-testid="followup-time" />
        </label>
      </div>
      <p className="text-xs text-zinc-600 -mt-2" data-testid="followup-summary">
        {cfg.enabled
          ? `Goes out ${cfg.days_after || "?"} day${cfg.days_after === 1 ? "" : "s"} after the first review text, at ${fmtTime(cfg.send_time)}. ${cfg.pending_count} customer${cfg.pending_count === 1 ? "" : "s"} currently waiting.`
          : "Off. No follow-ups will be sent."}
      </p>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-zinc-500">Message ({"{first_name}"}, {"{tech_credit}"}, {"{review_url}"})</span>
          <button type="button" onClick={() => set({ template: cfg.default_template })}
            className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1" data-testid="followup-reset">
            <RotateCcw className="w-3 h-3" /> Default
          </button>
        </div>
        <textarea rows={4} value={cfg.template} onChange={(e) => set({ template: e.target.value })}
          className="w-full border border-zinc-200 rounded-lg p-3 text-sm font-mono" data-testid="followup-template" />
        <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-700 whitespace-pre-wrap border mt-2" data-testid="followup-preview">
          {preview(cfg.template)}
        </div>
      </div>

      <button onClick={save} disabled={saving}
        className="w-full bg-[#207024] text-white py-3 rounded-xl font-semibold hover:bg-[#185a1c] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="save-followup-btn">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Follow-up
      </button>
    </div>
  );
};
